import type { SessionParticipant } from '~/gql/sessions/sessions'
import { DropoutReason, UserProgress } from '@zeus/index'
import { USER_PROGRESS_KEYS } from '~/pages/backoffice/admin/user/user-progress'

/** Un abandon déclaré (#324) prime sur l'avancement : c'est ce qu'on lit d'abord. */
export const DROPPED = 'DROPPED'

export type ParticipantStatus = UserProgress | typeof DROPPED

export const PARTICIPANT_STATE_VARIANTS = {
  [UserProgress.NOT_STARTED]: 'secondary',
  [UserProgress.IN_PROGRESS]: 'warning',
  [UserProgress.COMPLETED]: 'success',
  [DROPPED]: 'outline',
} as const satisfies Record<ParticipantStatus, string>

export const PARTICIPANT_STATUS_KEYS: Record<ParticipantStatus, string> = {
  ...USER_PROGRESS_KEYS,
  [DROPPED]: 'sessionDetail.dropout.status',
}

export const DROPOUT_REASON_KEYS: Record<DropoutReason, string> = {
  [DropoutReason.PERSONAL]: 'sessionDetail.dropout.reason.personal',
  [DropoutReason.PROFESSIONAL]: 'sessionDetail.dropout.reason.professional',
  [DropoutReason.HEALTH]: 'sessionDetail.dropout.reason.health',
  [DropoutReason.CONTENT]: 'sessionDetail.dropout.reason.content',
  [DropoutReason.TECHNICAL]: 'sessionDetail.dropout.reason.technical',
  [DropoutReason.OTHER]: 'sessionDetail.dropout.reason.other',
}

export function participantStatus(participant: Pick<SessionParticipant, 'state' | 'droppedAt'>): ParticipantStatus {
  return participant.droppedAt ? DROPPED : participant.state
}

/** Les participants dans cet état ; tous quand aucun n'est choisi. */
export function participantsInState(participants: readonly SessionParticipant[], state: ParticipantStatus | ''): SessionParticipant[] {
  return state ? participants.filter(p => participantStatus(p) === state) : [...participants]
}

/**
 * Taux d'abandon (#324) : abandons déclarés ÷ inscrits, en pourcentage arrondi.
 * Qualiopi (indicateur 2) demande d'en publier la méthode : la voici, et l'écran
 * la redit sous le chiffre. `null` sans inscrit, plutôt qu'un 0 % trompeur.
 */
export function dropoutRate(participants: readonly Pick<SessionParticipant, 'droppedAt'>[]): number | null {
  if (participants.length === 0) return null
  const dropped = participants.filter(p => p.droppedAt).length
  return Math.round((dropped / participants.length) * 1000) / 10
}
